interface Dimensions {
  width: number;
  height: number;
  depth: number;
}

// Build space limits in mm
export const PA12_BUILD_SPACE = {
  width: 190,
  height: 240,
  depth: 310
};

export const SILICONE_BUILD_SPACE = {
  width: 297,
  height: 210,
  depth: 200
};

export const validateBuildSpace = (dimensions: Dimensions, isSilicone: boolean): { valid: boolean; message?: string } => {
  const limits = isSilicone ? SILICONE_BUILD_SPACE : PA12_BUILD_SPACE;
  const materialName = isSilicone ? "Silikon" : "PA12";

  // Convert dimensions from cm to mm for validation
  const widthMm = dimensions.width;
  const heightMm = dimensions.height;
  const depthMm = dimensions.depth;

  // Check if any dimension exceeds the build space
  if (widthMm > limits.width || heightMm > limits.height || depthMm > limits.depth) {
    return {
      valid: false,
      message: `Modell überschreitet den maximalen ${materialName}-Bauraum`
    };
  }

  return { valid: true };
};

export const calculatePA12Price = (dimensions: Dimensions, quantity: number = 1): number => {
  // Dimensions are already in cm
  const width = dimensions.width;
  const height = dimensions.height;
  const depth = dimensions.depth;
  
  // PA12 price formula: X * Y * (Z+6) * 0.0002039 where Y is height+6
  const basePrice = width * (height + 6) * (depth) * 0.0002039;

  return basePrice * quantity;
};

export const calculateStandardPrice = (
  volumeCm3: number,
  quantity: number = 1,
  boundingBoxZMM: number = 0,
): number => {
  // Original-Excel-Parameter (direkt aus den Zellenformeln extrahiert)
  const DENSITY = 1.3;                     // Fix aus "A3*1.3" in Excel
  const MATERIAL_FACTOR = 2;               // Aus "(EK*Faktor_2)" in Zelle A27
  const SUPPORT_FACTOR = 2.5;              // Aus "*Faktor_2,5_Support" in Zelle A28
  const MACHINE_FACTOR = 2;                // Aus "*_Faktor_2" in Zelle A29
  const HANDLING_HOURS = 0.5;              // Aus "0.5*60" in Zelle A30
  const HANDLING_RATE_PER_HOUR = 60;       // €/Stunde (implizit in Excel)
  const SURCHARGE_FACTOR = (1/60) * 60;    // Aus "=I3*(1/60)*60" in Zelle A31

  const MATERIAL_PRICE_PER_KG = 450;       // EK: 450€ pro kg
  const SUPPORT_PRICE_PER_KG = 165;        // Support: 165€ pro kg

  // Convert cm³ to mm³ for consistency
  const volumeMM3 = volumeCm3 * 1000

  // 1. Materialkosten (Excel: =(A3*1.3/1.000.000)*I3*(EK*2)
  const materialCost = 
    (volumeMM3 * DENSITY / 1_000_000) * quantity * 
    (MATERIAL_PRICE_PER_KG * MATERIAL_FACTOR);
  // 2. Supportkosten (Excel: =((A3*1.3/1.000.000)*I3*2.5)*(SupportEK*2)
  const supportCost = 
    ((volumeMM3 * DENSITY / 1_000_000) * 
    quantity * SUPPORT_FACTOR) * 
    (SUPPORT_PRICE_PER_KG * MATERIAL_FACTOR);

  // 3. Maschinenlaufzeit (Excel: =(⌈D3/10⌉×10×2)×Anzahl) - D3 ist die Z-Dimension in mm
  const machineTimeCost = 
    (boundingBoxZMM / 10) * 10 * MACHINE_FACTOR; // Z-Dimension (depth) in mm

  // 4. Pauschale Handling (Excel: =(0.5*60)
  const handlingCost = 
    HANDLING_HOURS * HANDLING_RATE_PER_HOUR; // = 0.5 * 60

  // 5. Bestellmengen-Mehraufwand (Excel: =I3*(1/60)*60)
  const orderSurcharge = 
    quantity * SURCHARGE_FACTOR; // = I3 * 1

  // Summe (Excel: =SUM(A27:A31)
  const totalCost = materialCost + supportCost + machineTimeCost + handlingCost + orderSurcharge;
  return Number(totalCost.toFixed(2));
};

export const PROCESSING_FEE_PA12 = 15;
export const SHIPPING_COST = 17;